import styled from '@emotion/styled'
import type { FC } from 'react'

import Image from '@segment/matcha/components/Image'

import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import TabsLayoutBase, * as TabsLayout from '@segment/matcha/recipes/TabsLayout'
import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import useTabs from '@segment/matcha/recipes/Tabs/useTabs'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

import queryData1Webp from './assets/QueryData-1.webp'
import queryData1Png2x from './assets/QueryData-1@2x.png'
import queryData1Webp2x from './assets/QueryData-1@2x.webp'

import queryData2Webp from './assets/QueryData-2.webp'
import queryData2Png2x from './assets/QueryData-2@2x.png'
import queryData2Webp2x from './assets/QueryData-2@2x.webp'

import queryData3Webp from './assets/QueryData-3.webp'
import queryData3Png2x from './assets/QueryData-3@2x.png'
import queryData3Webp2x from './assets/QueryData-3@2x.webp'

const copy = {
  heading: `Query and access your data`,
  description: `We’ve built APIs to make querying & accessing your data super easy.`,
}

const tabs = [
  {
    key: `QUERY_DATA_SLIDE_KEY`,
    heading: `Query your data in SQL`,
    description: `We’ll automatically load your analytics data into your data warehouse (such as Amazon Redshift, Google BigQuery, Snowflake, and more).`,
    link: { text: `Explore the Warehouses product`, url: `/product/warehouses/` },
    image: {
      png2x: queryData1Png2x,
      webp: queryData1Webp,
      webp2x: queryData1Webp2x,
      width: 660,
      height: 521,
    },
  },
  {
    key: `BACKUP_DATA_SLIDE_KEY`,
    heading: `Backup & download your data`,
    description: `We’ll automatically backup your data to your storage provider, such as AWS S3 or Google Cloud Storage, for easy download and use.`,
    link: {
      text: `Explore the S3 integration`,
      url: `https://segment.com/docs/connections/storage/catalog/aws-s3/`,
    },
    image: {
      dropShadow: true,
      png2x: queryData2Png2x,
      webp: queryData2Webp,
      webp2x: queryData2Webp2x,
      width: 589,
      height: 389,
    },
  },
  {
    key: `ACCESS_PROFILES_SLIDE_KEY`,
    heading: `Access your real-time user profiles`,
    description: `Programmatically access your user profiles to power personalized experiences.`,
    link: { text: `Explore the Profile API`, url: `https://segment.com/docs/personas/profile-api/` },
    image: {
      dropShadow: true,
      png2x: queryData3Png2x,
      webp: queryData3Webp,
      webp2x: queryData3Webp2x,
      width: 589,
      height: 389,
    },
  },
]

type Tab = typeof tabs[number]

const QueryData = () => {
  const { getTrackProps, getControlProps, getPanelProps, activeTabKey } = useTabs(0, tabs.length, `query-data`)

  return (
    <SectionBase variant="centered">
      <Section.Copy>
        <CopyTextBase centered={true}>
          <CopyText.Heading tag="h2" variant="h2">
            {copy.heading}
          </CopyText.Heading>
          <CopyText.Description>{copy.description}</CopyText.Description>
        </CopyTextBase>
      </Section.Copy>
      <Section.Contents>
        <TabsLayoutBase reverse={true}>
          <TabsLayout.Controls {...getTrackProps()}>
            {tabs.map((tab, index) => (
              <TabsLayout.Control key={tab.key} {...getControlProps(index)}>
                {tab.heading}
              </TabsLayout.Control>
            ))}
          </TabsLayout.Controls>

          {tabs.map((tab, index) => (
            <TabsLayout.Content {...getPanelProps(index)} active={index === activeTabKey} key={tab.heading}>
              <TabsLayout.Copy>
                <SlideText tab={tab} />
              </TabsLayout.Copy>
              <TabsLayout.Media>
                <SlideImage {...tab.image} alt={tab.heading} />
              </TabsLayout.Media>
            </TabsLayout.Content>
          ))}
        </TabsLayoutBase>
      </Section.Contents>
    </SectionBase>
  )
}

export default QueryData

type SlideImageProps = Tab[`image`] & {
  alt: string
}

const SlideImage: FC<SlideImageProps> = ({ dropShadow, width, height, png2x, webp, webp2x, alt }) => {
  const Wrapper = dropShadow ? Picture : `picture`

  return (
    <Wrapper>
      <source type="image/webp" srcSet={`${webp}, ${webp2x} 2x`} />
      <Image alt={alt} width={width} height={height} src={png2x} loading="lazy" />
    </Wrapper>
  )
}

interface SlideTextProps {
  tab: Tab
}

const SlideText: FC<SlideTextProps> = ({ tab }) => {
  const pageConfig = usePageConfig()

  return (
    <CopyTextBase>
      <CopyText.Heading variant="h2">{tab.heading}</CopyText.Heading>
      <CopyText.Description variant="pLarge">{tab.description}</CopyText.Description>
      <CopyText.Link href={getAbsoluteUrl(tab.link.url, pageConfig)}>{tab.link.text}</CopyText.Link>
    </CopyTextBase>
  )
}

const Picture = styled.picture`
  filter: drop-shadow(${({ theme }) => theme.shadows[4]});
`
